// import { LocationMapDialog } from '@/components/controls';
import { LoadingIcon, LocationMapDialog } from '@/components/controls';
import { getGroupType, log, spin, toProperCase } from '@/utils';
import {
  Autorenew as AutorenewIcon,
  Done as DoneIcon,
} from '@mui/icons-material';
import { Masonry } from '@mui/lab';
import {
  alpha,
  Box,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import { Fragment, useMemo, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import { useParams } from 'react-router';
import { MissingPerson } from './MissingPerson';
import { SharedMissingPerson } from './SharedMissingPerson';
import { SharedMissingPersonEditor } from './SharedMissingPersonEditor';
import { useDeleteSharedMissingPerson } from './useDeleteSharedMissingPerson';
import { useNicheMissingPerson } from './useNicheMissingPerson';
import { useNicheMissingPersons } from './useNicheMissingPersons';
import { useSharedMissingPersons } from './useSharedMissingPersons';

export function MissingPersons({ groupCodes, onComplete }) {
  const {
    query: {
      data: missingPersons,
      isLoading: missingPersonsLoading,
      isFetching: missingPersonsFetching,
      refetch: refetchMissingPersons,
    },
  } = useNicheMissingPersons(getGroupType('SECTOR'), groupCodes);
  const {
    query: {
      data: highRiskMissingPersons,
      isLoading: highRiskMissingPersonsLoading,
      isFetching: highRiskMissingPersonsFetching,
      refetch: refetchHighRiskMissingPersons,
    },
  } = useNicheMissingPersons(getGroupType('BASIC_COMMAND_UNIT'), groupCodes, {
    highRisk: true,
  });
  const {
    query: {
      data: sharedMissingPersons,
      isLoading: sharedMissingPersonsLoading,
      isFetching: sharedMissingPersonsFetching,
      refetch: refetchSharedMissingPersons,
    },
  } = useSharedMissingPersons(groupCodes);
  const sectorMissingPersons = useMemo(
    () =>
      (missingPersons ?? []).filter(
        ({ id }) =>
          !(sharedMissingPersons ?? []).some(({ personId }) => personId === id),
      ),
    [missingPersons, sharedMissingPersons],
  );
  const { width, ref } = useResizeDetector();
  const highRiskColumns = useMemo(() => Math.floor(width / 328) || 1, [width]);
  const lowRiskColumns = useMemo(() => Math.floor(width / 248) || 1, [width]);
  const [shareIds, setShareIds] = useState(null);
  const [selectedPersonId, setSelectedPersonId] = useState(null);
  const {
    query: { data: selectedMissingPerson },
  } = useNicheMissingPerson(selectedPersonId);
  const { mutate: deleteSharedMissingPerson } = useDeleteSharedMissingPerson();
  const { code } = useParams();

  function handleRefreshClick() {
    refetchMissingPersons();
    refetchHighRiskMissingPersons();
    refetchSharedMissingPersons();
  }

  const handleShowMapClick = (id) => () => {
    setSelectedPersonId(id);
  };

  function handleMapClose() {
    setSelectedPersonId(null);
  }

  function handleCompleteClick() {
    log('View', 'Missing Persons', { code, groupCodes });
    onComplete();
  }

  const handleShareClick = (code, personId) => () => {
    setShareIds({ code, personId });
  };

  function handleEditorClose() {
    setShareIds(null);
  }

  const handleDeleteClick = (code) => () => {
    deleteSharedMissingPerson(code);
  };

  return (
    <Box sx={{ px: 1 }}>
      <Stack
        direction="row"
        spacing={1}
        sx={(theme) => ({
          alignItems: 'center',
          position: 'sticky',
          top: 48,
          bgcolor: alpha(theme.palette.background.paper, 0.8),
          zIndex: 700,
          pl: 1,
          py: 1,
        })}
      >
        <Typography variant="h6">Missing Persons</Typography>
        <Box>
          <Tooltip title="Done" placement="right">
            <IconButton onClick={handleCompleteClick}>
              <DoneIcon fontSize="inherit" />
            </IconButton>
          </Tooltip>
        </Box>
        <Box sx={{ flexGrow: 1 }} />
        <Box>
          <Tooltip title="Refresh" placement="bottom">
            <Box component="span">
              <IconButton
                onClick={handleRefreshClick}
                disabled={
                  missingPersonsFetching ||
                  missingPersonsLoading ||
                  highRiskMissingPersonsLoading ||
                  highRiskMissingPersonsFetching ||
                  sharedMissingPersonsLoading ||
                  sharedMissingPersonsFetching
                }
              >
                <AutorenewIcon
                  fontSize="inherit"
                  sx={
                    missingPersonsFetching ||
                    missingPersonsLoading ||
                    highRiskMissingPersonsLoading ||
                    highRiskMissingPersonsFetching ||
                    sharedMissingPersonsLoading ||
                    sharedMissingPersonsFetching
                      ? { animation: `${spin} 2s linear infinite` }
                      : undefined
                  }
                />
              </IconButton>
            </Box>
          </Tooltip>
        </Box>
      </Stack>
      <Stack spacing={1} sx={{ pb: 1 }} ref={ref}>
        {(highRiskMissingPersons?.length > 0 ||
          highRiskMissingPersonsLoading ||
          highRiskMissingPersonsFetching) && (
          <Fragment>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ pl: 1 }}>
                High Risk in my BCU
              </Typography>
              <LoadingIcon
                isLoading={
                  highRiskMissingPersonsLoading ||
                  highRiskMissingPersonsFetching
                }
              />
            </Stack>
            <Masonry spacing={1} columns={highRiskColumns}>
              {highRiskMissingPersons.map((missingPerson) => (
                <MissingPerson
                  key={missingPerson.id}
                  missingPerson={missingPerson}
                  onShowMapClick={handleShowMapClick(missingPerson.id)}
                  onShareClick={handleShareClick}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {(sharedMissingPersons?.length > 0 ||
          sharedMissingPersonsLoading ||
          sharedMissingPersonsFetching) && (
          <Fragment>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ pl: 1 }}>
                Shared
              </Typography>
              <LoadingIcon
                isLoading={
                  sharedMissingPersonsLoading || sharedMissingPersonsFetching
                }
              />
            </Stack>
            <Masonry spacing={1} columns={lowRiskColumns}>
              {sharedMissingPersons.map(({ code, personId, reason }) => (
                <SharedMissingPerson
                  key={personId}
                  personId={personId}
                  reason={reason}
                  onShowMapClick={handleShowMapClick(personId)}
                  onShareClick={handleShareClick}
                  onDeleteClick={handleDeleteClick(code)}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
        {(sectorMissingPersons?.length > 0 ||
          missingPersonsLoading ||
          missingPersonsFetching) && (
          <Fragment>
            <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
              <Typography variant="subtitle1" sx={{ pl: 1 }}>
                Medium & Low Risk in my Sector
              </Typography>
              <LoadingIcon
                isLoading={missingPersonsLoading || missingPersonsFetching}
              />
            </Stack>
            <Masonry spacing={1} columns={lowRiskColumns}>
              {sectorMissingPersons.map((missingPerson) => (
                <MissingPerson
                  key={missingPerson.id}
                  missingPerson={missingPerson}
                  onShowMapClick={handleShowMapClick(missingPerson.id)}
                  onShareClick={handleShareClick}
                />
              ))}
            </Masonry>
          </Fragment>
        )}
      </Stack>
      <SharedMissingPersonEditor
        ids={shareIds}
        isOpen={Boolean(shareIds)}
        onClose={handleEditorClose}
      />
      <LocationMapDialog
        addresses={selectedMissingPerson?.addresses}
        title={`Points of interest for ${toProperCase(
          [selectedMissingPerson?.forenames, selectedMissingPerson?.surname]
            .filter(Boolean)
            .join(' '),
        )}`}
        isOpen={Boolean(selectedPersonId)}
        onClose={handleMapClose}
      />
    </Box>
  );
}
